import { useEffect, useLayoutEffect, useMemo } from 'react';
import { matchRoutes, Outlet, useLocation } from 'react-router-dom';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, Container, CssBaseline, Toolbar, useMediaQuery } from '@mui/material';

// project imports
import Header from './Header';
import Sidebar from './Sidebar';
import HorizontalBar from './HorizontalBar';
// import Customization from '../Customization';
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import SetPasswordDialog from 'views/pages/authentication/auth-forms/SetPasswordDialog';

import navigation from 'menu-items';
import LAYOUT_CONST from 'constant';
import useConfig from 'hooks/useConfig';
import { drawerWidth, horizontalBarHeight, horizontalHeaderHeight, mainHeaderHeight } from 'store/constant';
import { openDrawer } from 'store/slices/menu';
import { useDispatch, useSelector } from 'store';

// assets
import { IconChevronRight } from '@tabler/icons-react';
import useAuth from 'hooks/useAuth';
import SessionTimeoutPassword from 'ui-component/modals/SessionTimeoutPassword';
// import pageItems from 'page-items';
// import RolePermission from 'utils/permission';
import BackDropLoader from 'ui-component/BackDropLoader';
import menuItems from 'menu-items';
import pageItems from 'page-items';
import usePermission from 'hooks/usePermission';
import { fetchClientSettings } from 'store/slices/settings';

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open, layout }) => ({
    ...theme.typography.mainContent,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    ...(!open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter + 200
        }),
        [theme.breakpoints.up('md')]: {
            marginLeft: layout === LAYOUT_CONST.VERTICAL_LAYOUT ? -(drawerWidth - 72) : '20px',
            width: `calc(100% - ${drawerWidth}px)`,
            marginTop: layout === LAYOUT_CONST.HORIZONTAL_LAYOUT ? horizontalHeaderHeight + horizontalBarHeight : mainHeaderHeight
        }
    }),
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.shorter + 200
        }),
        marginLeft: layout === LAYOUT_CONST.HORIZONTAL_LAYOUT ? '20px' : 0,
        marginTop: layout === LAYOUT_CONST.HORIZONTAL_LAYOUT ? horizontalHeaderHeight + horizontalBarHeight : mainHeaderHeight,
        width: `calc(100% - ${drawerWidth}px)`,
        [theme.breakpoints.up('md')]: {
            marginTop: layout === LAYOUT_CONST.HORIZONTAL_LAYOUT ? horizontalHeaderHeight + horizontalBarHeight : mainHeaderHeight
        }
    }),
    [theme.breakpoints.down('md')]: {
        marginLeft: '20px',
        padding: '16px',
        marginTop: mainHeaderHeight,
        ...(!open && {
            width: `calc(100% - ${drawerWidth}px)`
        })
    },
    [theme.breakpoints.down('sm')]: {
        marginLeft: '10px',
        marginRight: '10px',
        padding: '16px',
        marginTop: mainHeaderHeight,
        ...(!open && {
            width: `calc(100% - ${drawerWidth}px)`
        })
    }
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const { isSessionTimeout, user } = useAuth();
    const theme = useTheme();

    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

    const dispatch = useDispatch();
    const { drawerOpen } = useSelector((state) => state.menu);
    const { drawerType, container, layout } = useConfig();

    // get the current path
    const location = useLocation();
    const currentPath = location.pathname;
    const { can } = usePermission({ isAdminNotAuthorized: false });

    const { clientSettings } = useSelector((state) => state.settings);
    const { trial = false } = clientSettings;

    useEffect(() => {
        dispatch(fetchClientSettings()).catch(() => {});
    }, [dispatch]);

    /**
     * This will redirect to default routes
     * If not authorized to view the page that is not belong to the menu
     */
    useLayoutEffect(() => {
        if (!user) return;

        const { items = [] } = pageItems;
        // Rank matching paths so detail pages take precedence over their parent pages.
        const routes = items
            .filter((i) => i.path || i.url)
            .map((i) => ({
                path: `${(i.path || i.url).replace(/\/?\*?$/, '')}/*`,
                item: i
            }));
        const matches = matchRoutes(routes, currentPath);
        const item = matches?.[0]?.route.item;

        if (item) {
            const resource = item?.permissions?.resource;
            const action = item?.permissions?.action;

            if (!trial && !can(action, resource)) {
                window.location.assign('/');
            }
        }
    }, [can, currentPath, trial, user]);

    /**
     * This will redirect to default routes for trial direct access
     * If not authorized to view the page
     */
    useLayoutEffect(() => {
        const { items = [] } = menuItems;
        const item = items.find((i) => i.url === currentPath);
        if (trial && item) {
            const { permissions } = item;
            if (permissions?.disableInTrial) {
                window.location.assign('/');
            }
        }
    }, [currentPath, trial]);

    useEffect(() => {
        if (drawerType === LAYOUT_CONST.DEFAULT_DRAWER) {
            dispatch(openDrawer(true));
        } else {
            dispatch(openDrawer(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawerType]);

    useEffect(() => {
        if (drawerType === LAYOUT_CONST.DEFAULT_DRAWER) {
            dispatch(openDrawer(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (matchDownMd) {
            dispatch(openDrawer(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchDownMd]);

    const condition = layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && !matchDownMd;

    const header = useMemo(
        () => (
            <Toolbar
                sx={{
                    boxSizing: 'border-box',
                    height: condition ? horizontalHeaderHeight : mainHeaderHeight,
                    minHeight: `${condition ? horizontalHeaderHeight : mainHeaderHeight}px !important`,
                    p: condition ? '10px' : '16px',
                    bgcolor: theme.palette.mode !== 'dark' ? theme.palette.secondary.main : theme.palette.background.default
                }}
            >
                <Header />
            </Toolbar>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [layout, matchDownMd]
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            {/* header */}
            <AppBar
                enableColorOnDark
                position="fixed"
                color="inherit"
                elevation={0}
                sx={{
                    height: condition ? horizontalHeaderHeight : mainHeaderHeight,
                    bgcolor: theme.palette.background.default
                }}
            >
                {header}
            </AppBar>

            {/* horizontal menu-list bar */}
            {layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && !matchDownMd && <HorizontalBar />}

            {/* drawer */}
            {(layout === LAYOUT_CONST.VERTICAL_LAYOUT || matchDownMd) && <Sidebar />}

            {/* main content */}
            <Main theme={theme} open={drawerOpen} layout={layout}>
                <Container maxWidth={container ? 'lg' : false} {...(!container && { sx: { px: { xs: 0 } } })}>
                    {/* breadcrumb */}
                    <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
                    <Outlet />
                </Container>
            </Main>
            {/* <Customization /> */}

            {/* Re Enter Password if Session Timeout */}
            <SessionTimeoutPassword toggleDialog={isSessionTimeout} />

            <SetPasswordDialog />

            {/* For Back Drop */}
            <BackDropLoader />
        </Box>
    );
};

export default MainLayout;
